import { useApi } from '../../core/api/api.context';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { RestError } from '@azure/core-http';
import {
  OpenLockerResponseDto,
  LockersControllerEnableResponse,
  SpacesControllerEnableResponse,
  DoorDto,
  CanIOpenDeliveryContainerDto,
  KnownDeliveryContainerDtoType,
  EditDeliveryContainerDto,
  DeliveryContainerDto,
  LockerDto,
  SpaceDto,
  TimelineDto,
  TowerModelDto,
  DeliveryContainerTransactionTypesDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { QueryKeys } from '../../core/queries/query-keys.constants';
import { REFERENCE_LIST_STALE_TIME } from '../../core/queries/query-helpers';
import { Locker } from './models/locker.model';
import { Space } from './models/space.model';
import { DeliveryContainer } from './models/delivery-container.model';

export function useMutationOpenLocker() {
  const api = useApi();

  return useMutation<OpenLockerResponseDto, RestError, { lockerID: string; doorIDs?: string[] }>(
    async ({ lockerID, doorIDs }) => {
      return await api.lockersController.openLocker(lockerID, { doorIDs });
    }
  );
}
export function useMutationEnableDisableLocker() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    LockersControllerEnableResponse,
    RestError,
    {
      lockerID: string;
      enable: boolean;
      note: {
        noteContent: string;
        isPrivate: boolean;
      };
    }
  >(
    async ({ lockerID, enable, note: { noteContent, isPrivate } }) => {
      const response = await api.lockersController.enable(lockerID, {
        enable,
        note: {
          content: noteContent,
          private: isPrivate
        }
      });
      return response;
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
      }
    }
  );
}

export function useMutationEditDeliveryContainer() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<DeliveryContainer, RestError, { deliveryContainerID: string; dto: EditDeliveryContainerDto }>(
    async ({ deliveryContainerID: lockerID, dto }) => {
      const response = await api.deliveryContainersController.edit(lockerID, dto);
      return new DeliveryContainer(response, response.type as KnownDeliveryContainerDtoType, response.module);
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
      }
    }
  );
}

export function useMutationReserveContainerForParty() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<DeliveryContainerDto, RestError, { deliveryContainerID: string; partyIDs: string[] }>(
    async ({ deliveryContainerID, partyIDs }) => {
      return await api.deliveryContainersController.reserveForParties(deliveryContainerID, { partyIDs });
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
      }
    }
  );
}

export function useMutationRemoveReservationForParty() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<DeliveryContainerDto, RestError, { deliveryContainerID: string; partyIDs: string[] }>(
    async ({ deliveryContainerID, partyIDs }) => {
      return await api.deliveryContainersController.removeReservationForParties(deliveryContainerID, { partyIDs });
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
      }
    }
  );
}

export function useQueryGetLocker(lockerID: string, enabled = true) {
  const api = useApi();

  return useQuery<LockerDto | undefined, RestError, Locker | undefined>(
    [QueryKeys.DELIVERY_CONTAINERS, lockerID],
    async () => {
      if (lockerID === undefined) {
        return undefined;
      }
      return await api.lockersController.get(lockerID);
    },
    {
      enabled,
      select: (lockerDto) => (lockerDto ? new Locker(lockerDto) : undefined)
    }
  );
}

export function useQueryGetSpace(spaceID: string, enabled: boolean) {
  const api = useApi();

  return useQuery<SpaceDto | undefined, RestError, Space | undefined>(
    [QueryKeys.DELIVERY_CONTAINERS, spaceID],
    async () => {
      if (spaceID === undefined) return undefined;
      return await api.spacesController.get(spaceID);
    },
    {
      enabled,
      select: (spaceDto) => (spaceDto ? new Space(spaceDto) : undefined)
    }
  );
}

export function useQueryGetDeliveryContainerType(deliveryContainerID: string) {
  const api = useApi();

  return useQuery<KnownDeliveryContainerDtoType | undefined, RestError>(
    [QueryKeys.DELIVERY_CONTAINERS, deliveryContainerID, 'type'],
    async () => {
      if (!deliveryContainerID) return undefined;
      const response = await api.deliveryContainersController.get(deliveryContainerID);
      return response.type as KnownDeliveryContainerDtoType;
    }
  );
}

export function useQueryGetDeliveryContainerTransactionTypes(deliveryContainerID: string) {
  const api = useApi();

  return useQuery<DeliveryContainerTransactionTypesDto | undefined, RestError>(
    [QueryKeys.DELIVERY_CONTAINERS, deliveryContainerID, QueryKeys.TRANSACTION_TYPES],
    async () => {
      if (!deliveryContainerID) return undefined;
      return await api.deliveryContainersController.getTransactionTypes(deliveryContainerID);
    }
  );
}

export function useMutationEnableDisableSpace() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    SpacesControllerEnableResponse,
    RestError,
    {
      spaceID: string;
      enable: boolean;
      note: {
        noteContent: string;
        isPrivate: boolean;
      };
    }
  >(
    async ({ spaceID, enable, note: { noteContent, isPrivate } }) => {
      const response = await api.spacesController.enable(spaceID, {
        enable,
        note: {
          content: noteContent,
          private: isPrivate
        }
      });
      return response;
    },
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
      }
    }
  );
}

export function useQueryListLockerDoors(lockerID?: string) {
  const api = useApi();

  return useQuery<DoorDto[], RestError>(
    [QueryKeys.DELIVERY_CONTAINER_ACCESS_LAYERS, lockerID],
    async () => {
      if (lockerID === undefined) {
        return [];
      }
      return (await api.doorsController.list({ lockerID })).items ?? [];
    },
    {
      enabled: !!lockerID
    }
  );
}

export function useQueryGetLockerTimeline(
  deliveryContainerID: string,
  window: { from: Date; to: Date },
  enabled: boolean
) {
  const api = useApi();

  return useQuery<TimelineDto | undefined, RestError>(
    [
      QueryKeys.DELIVERY_CONTAINERS,
      deliveryContainerID,
      QueryKeys.TIMELINE,
      window.from.toISOString(),
      window.to.toISOString()
    ],
    async () => {
      if (!deliveryContainerID) return undefined;
      return await api.deliveryContainersTimelineController.getTimeline(deliveryContainerID, {
        from: window.from,
        to: window.to
      });
    },
    { enabled, keepPreviousData: true }
  );
}

export function useQueryCanIOpenLocker(lockerID: string) {
  const api = useApi();

  return useQuery<CanIOpenDeliveryContainerDto, RestError>(
    [QueryKeys.DELIVERY_CONTAINERS, lockerID, QueryKeys.CAN_I_OPEN_LOCKER],
    async () => {
      const response = await api.deliveryContainersController.canIOpen(lockerID);
      return response;
    }
  );
}

export function useQueryListTowerModels() {
  const api = useApi();

  return useQuery<TowerModelDto[], RestError>(
    [QueryKeys.TOWER_MODELS],
    async () => {
      return await api.towerModelsController.list();
    },
    { staleTime: REFERENCE_LIST_STALE_TIME }
  );
}

export function useQueryGetTowerModel(modelName: string | undefined) {
  const api = useApi();

  return useQuery<TowerModelDto | undefined, RestError>(
    [QueryKeys.TOWER_MODELS, modelName],
    async () => {
      if (!modelName) return undefined;
      return await api.towerModelsController.getByName(modelName);
    },
    { enabled: !!modelName, staleTime: REFERENCE_LIST_STALE_TIME }
  );
}

export interface BulkEditDeliveryContainerDeliveryTypesItemDto {
  containerID: string;
  selectedInternalProjectModules?: string[];
  selectedExternalProjectModules?: string[];
}

export interface BulkEditDeliveryContainerDeliveryTypesDto {
  hubID: string;
  updates: BulkEditDeliveryContainerDeliveryTypesItemDto[];
}

export function useMutationBulkEditDeliveryContainerDeliveryTypes() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<DeliveryContainerDto[], RestError, BulkEditDeliveryContainerDeliveryTypesDto>(
    (dto) => api.deliveryContainersController.bulkEditDeliveryTypes(dto),
    {
      onSettled() {
        queryClient.invalidateQueries(QueryKeys.DELIVERY_CONTAINERS);
        queryClient.invalidateQueries(QueryKeys.HUBS_DETAILS);
      }
    }
  );
}
